/**
 * TASK API — Wrapper dei REST Liferay allineato a TasksResourceImpl.java.
 * I 5 metodi CRUD standard vengono da `createCrudApi`; sopra si aggiunge la
 * transizione di stato dedicata (`updateStatus`).
 *
 * Contratto backend (rest-openapi.yaml):
 *   - GET    /tasks/{groupId}       pagination server-side    -> Page<Task>
 *     Query params: page (1-based, default 1), pageSize (default 20),
 *                   sort (es. "expirationDate:asc"), search.
 *   - POST   /tasks/{groupId}  TaskInput                       -> Task
 *   - GET    /task/{taskId}                                     -> Task
 *   - PATCH  /task/{taskId}    TaskInput (parziale, NO status) -> Task
 *   - PATCH  /task/{taskId}/status  TaskStatusInput            -> Task
 *   - DELETE /task/{taskId}                                     -> 200
 *
 * NB: path singolare per get/update/delete, plurale per list/create.
 * NB: param di ricerca si chiama `search` (non `q`).
 * Enum type/priority/status viaggiano come int32: il mapping string<->int
 * e' in taskMappers.ts e applicato ai boundary dai mapper. Lo `status` non passa
 * dall'`update` generico ma dall'endpoint dedicato `updateStatus` (vedi
 * `taskStatusToInput`); `taskUpdateToInput` ignora `taskId` anche se nel payload.
 */

import { httpClient } from '../../api/httpClient';
import { createCrudApi } from '../../store/createCrudApi';
import {
  taskFromDto,
  taskCreateToInput,
  taskUpdateToInput,
  taskStatusToInput,
  type TaskDto,
} from './taskMappers';
import type {
  Task,
  TaskCreatePayload,
  TaskListParams,
  TaskStatus,
  TaskUpdatePayload,
} from './taskTypes';

const SINGLE = '/task';

const base = createCrudApi<Task, TaskDto, TaskCreatePayload, TaskUpdatePayload, TaskListParams>({
  collection: '/tasks',
  single: SINGLE,
  fromDto: taskFromDto,
  createToInput: taskCreateToInput,
  updateId: (p) => p.taskId,
  updateToInput: taskUpdateToInput,
});

export const taskApi = {
  ...base,

  /**
   * Endpoint dedicato per la sola transizione di stato. Body `{status: int32}`
   * validato server-side via `TaskStatus.isValid` (BE risponde 400
   * `MandatoryStatus` su valore fuori-enum).
   */
  updateStatus: (taskId: number, status: TaskStatus): Promise<Task> =>
    httpClient
      .patch<TaskDto>(`${SINGLE}/${taskId}/status`, taskStatusToInput(status))
      .then((r) => taskFromDto(r.data)),
};
